import { AvailableLocale } from '@models/Lang'
import { QRCode, Typography } from 'antd'
import { CSSProperties } from 'react'
import {
  FieldHighlightProps,
  ResolvedWalletCard,
  WalletCardAsset,
  WalletField,
} from '../types'
import { WALLET_LABELS } from '../labels'
import { useWalletAssetUrl } from '../useWalletAssetUrl'
import WalletFieldZone from '../WalletFieldZone'

const { Text } = Typography

const CARD_WIDTH = 340

/** Faint hatched fill marking where an unset image would sit, so hovering its
 *  uploader still shows the admin the spot. */
const placeholderBand = (foregroundColor: string): CSSProperties => ({
  backgroundImage: `repeating-linear-gradient(135deg, ${foregroundColor}1a 0 10px, transparent 10px 20px)`,
})

interface Props extends FieldHighlightProps {
  card: ResolvedWalletCard
  logo: WalletCardAsset
  strip: WalletCardAsset
  /**
   * The pkpass `logoText`, drawn beside the logo — and only ever present when
   * an admin actually set a brand line. ApplePassBuilder omits the key
   * otherwise, so that a program which configures nothing keeps the pass it
   * already had; passing the fallback here would promise a line Wallet is
   * never going to draw. Named after the pkpass field so the two stay tied.
   */
  logoText?: string
  /** The back-office language selector's current locale, for label translation. */
  locale?: AvailableLocale
}

/**
 * Mirrors how Wallet actually draws a storeCard: background fill, logo +
 * optional logoText header, strip image band (only when the program uploaded
 * one — PassAssetService::OPTIONAL_FILES — else Wallet draws no band at all),
 * secondary fields as an equal-column grid (label in labelColor above the
 * value in foregroundColor) — Member / Card, plus Expires as a third column
 * when configured — then the barcode on its own white chip (Wallet never tints
 * the barcode) with the card number as its alt-text line beneath. Apple never
 * renders icon.png on the pass face (it's for notifications and the pass list),
 * so it has no place here either.
 */
const ApplePassFace = ({
  card,
  logo,
  strip,
  logoText,
  locale = 'en',
  activeField,
  onActiveFieldChange,
}: Props) => {
  const logoUrl = useWalletAssetUrl(logo)
  const stripUrl = useWalletAssetUrl(strip)
  const labels = WALLET_LABELS[locale]
  const highlight = { activeField, onActiveFieldChange }

  const labelStyle: CSSProperties = {
    color: card.labelColor,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  }
  const valueStyle: CSSProperties = {
    color: card.foregroundColor,
    fontSize: 15,
    fontWeight: 600,
  }

  // Only the first field carries the label/value highlight zones — the colors
  // apply to every field, so one representative each keeps the preview readable
  // instead of ringing three columns at once.
  const fields: {
    key: string
    label: string
    value: string
    labelField?: WalletField
    valueField?: WalletField
  }[] = [
    {
      key: 'member',
      label: labels.member,
      value: card.member.name,
      labelField: 'labelColor',
      valueField: 'foregroundColor',
    },
    { key: 'card', label: labels.cardNumber, value: card.member.cardNumber },
  ]
  if (card.showExpiry) {
    fields.push({
      key: 'expiry',
      label: labels.expires,
      value: card.member.expiryDate,
      valueField: 'showExpiry',
    })
  }

  return (
    <WalletFieldZone
      field="backgroundColor"
      radius={16}
      {...highlight}
      style={{
        width: CARD_WIDTH,
        overflow: 'hidden',
        backgroundColor: card.backgroundColor,
        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.22)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '16px 16px 12px',
        }}
      >
        {logoUrl ? (
          <WalletFieldZone field="logo" radius={6} {...highlight}>
            <img
              src={logoUrl}
              alt="Logo"
              style={{
                height: 32,
                maxWidth: 90,
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </WalletFieldZone>
        ) : (
          <WalletFieldZone field="logo" radius={8} {...highlight}>
            <div
              style={{
                height: 32,
                width: 32,
                borderRadius: 8,
                background: `${card.foregroundColor}26`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: card.foregroundColor,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {card.brandLine.charAt(0)}
            </div>
          </WalletFieldZone>
        )}
        {logoText ? (
          <WalletFieldZone field="brandLine" {...highlight}>
            <Text style={{ color: card.foregroundColor, fontWeight: 700 }}>
              {logoText}
            </Text>
          </WalletFieldZone>
        ) : (
          <></>
        )}
      </div>

      {stripUrl ? (
        <WalletFieldZone field="strip" radius={0} {...highlight}>
          <div
            style={{
              height: 110,
              backgroundImage: `url(${stripUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </WalletFieldZone>
      ) : activeField === 'strip' ? (
        // No strip uploaded: reveal the drop location only while its uploader
        // is active, so the resting pass stays faithful (Apple draws no band).
        <WalletFieldZone field="strip" radius={0} {...highlight}>
          <div
            style={{
              height: 110,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...placeholderBand(card.foregroundColor),
            }}
          >
            <span
              style={{
                color: card.foregroundColor,
                opacity: 0.7,
                fontSize: 11,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
              }}
            >
              Strip image
            </span>
          </div>
        </WalletFieldZone>
      ) : (
        <></>
      )}

      <div
        style={{
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', gap: 12 }}>
          {fields.map(({ key, label, value, labelField, valueField }) => (
            <div
              key={key}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              {labelField ? (
                <WalletFieldZone
                  field={labelField}
                  {...highlight}
                  style={{ alignSelf: 'flex-start' }}
                >
                  <span style={labelStyle}>{label}</span>
                </WalletFieldZone>
              ) : (
                <span style={labelStyle}>{label}</span>
              )}
              {valueField ? (
                <WalletFieldZone
                  field={valueField}
                  {...highlight}
                  style={{ alignSelf: 'flex-start' }}
                >
                  <span style={valueStyle}>{value}</span>
                </WalletFieldZone>
              ) : (
                <span style={valueStyle}>{value}</span>
              )}
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 10 }}>
            <QRCode
              value={`plethore:member:${card.member.cardNumber}`}
              size={120}
              bordered={false}
              color="#000"
              bgColor="#fff"
            />
          </div>
          <Text
            style={{
              color: card.foregroundColor,
              fontSize: 11,
              letterSpacing: 0.4,
            }}
          >
            {card.member.cardNumber}
          </Text>
        </div>
      </div>
    </WalletFieldZone>
  )
}

export default ApplePassFace
